import React, { useState, useCallback } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { logger } from 'winston-logger';
import { MassPrintFdcService } from './mass-print-fdc.service';
import { MassPrintButtonProps } from './mass-print-types';

/**
 * Button component for mass printing selected FDC forms.
 * Handles the print request, PDF download, and user feedback.
 *
 * @param props - Component props
 * @returns Rendered mass print button
 *
 * @example
 * ```tsx
 * <MassPrintButton
 *   selectedTaskIds={[123, 456, 789]}
 *   onPrintComplete={() => clearSelection()}
 *   disabled={false}
 * />
 * ```
 */
const MassPrintButton: React.FC<MassPrintButtonProps> = ({
  selectedTaskIds,
  onPrintComplete,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = useCallback(async () => {
    try {
      setIsPrinting(true);

      // Validate task IDs
      MassPrintFdcService.validateTaskIds(selectedTaskIds);

      logger.info('Starting mass print for FDC forms', {
        taskIds: selectedTaskIds,
        count: selectedTaskIds.length,
      });

      // Call API to generate merged PDF
      const pdfBlob = await MassPrintFdcService.printFdcForms(selectedTaskIds);

      // Trigger download
      const filename = `fdc-forms-${new Date().toISOString().split('T')[0]}.pdf`;
      MassPrintFdcService.downloadPdf(pdfBlob, filename);

      // Show success notification
      dispatch(
        notifySuccess(
          `Successfully printed ${selectedTaskIds.length} FDC form${
            selectedTaskIds.length > 1 ? 's' : ''
          }`,
        ),
      );

      // Clear selection after successful print
      onPrintComplete();

      logger.info('Mass print completed successfully', {
        taskIds: selectedTaskIds,
        filename,
      });
    } catch (error) {
      logger.error('Failed to mass print FDC forms', {
        error,
        taskIds: selectedTaskIds,
        message: error instanceof Error ? error.message : 'Unknown error',
      });

      // Show error notification
      dispatch(
        notifyError(
          error instanceof Error && error.message
            ? `Failed to print FDC forms: ${error.message}`
            : 'Failed to print FDC forms. Please try again.',
        ),
      );
    } finally {
      setIsPrinting(false);
    }
  }, [selectedTaskIds, onPrintComplete, dispatch]);

  const isDisabled = disabled || isPrinting || selectedTaskIds.length === 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="primary"
        onClick={handlePrint}
        disabled={isDisabled}
        fullWidth
        startIcon={isPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
        sx={{ minWidth: 150 }}
      >
        {isPrinting
          ? 'Printing...'
          : `Print${selectedTaskIds.length > 0 ? ` (${selectedTaskIds.length})` : ''}`}
      </Button>
    </Box>
  );
};

export default MassPrintButton;
